/**
 * ShareholdersDirectors Component
 * Manages director information input for company onboarding
 */

import { FC, useCallback, useMemo } from "react";
import { useStyles } from "./ShareholdersDirectors.styles";
import { ActionIcon, Group, NumberInput, Text, Tooltip } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { ShareholderDirector } from "../../features/merchant/rep";
import { ShareholderDirectorField } from "@funded-here-interface/common/src/constant/enum";
import {
  EMAIL_REGEX,
  InputGroup,
  NAME_REGEX,
} from "@funded-here-interface/common";

interface IProps {
  isReadOnly?: boolean;
  numOfShareholdersDirectors: number;
  onNumberOfShareholdersDirectorsChange: (value: number) => void;
  error?: string;
  shareholdersDirectors: ShareholderDirector[];
  updateShareholderDirectorValue: (
    index: number,
    field: ShareholderDirectorField,
    value: string
  ) => void;
  onRemoveDirector?: (index: number) => void;
  errors?: { [key: string]: string };
}

/**
 * Configuration for input fields to eliminate duplication
 */
interface FieldConfig {
  field: ShareholderDirectorField;
  label: string;
  idPrefix: string;
  regex: RegExp;
  getValue: (director: ShareholderDirector) => string;
  getError: (
    errors: { [key: string]: string },
    index: number
  ) => string | undefined;
}

const ShareholdersDirectors: FC<IProps> = (props) => {
  const {
    isReadOnly = false,
    numOfShareholdersDirectors,
    onNumberOfShareholdersDirectorsChange,
    error,
    shareholdersDirectors,
    updateShareholderDirectorValue,
    onRemoveDirector,
    errors = {},
  } = props;
  const { classes } = useStyles();

  /**
   * Prevents manual keyboard input on NumberInput
   * Users must use increment/decrement buttons
   */
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      event.preventDefault();
    },
    []
  );

  // Field configurations to eliminate duplication
  const fieldConfigs: FieldConfig[] = useMemo(
    () => [
      {
        field: ShareholderDirectorField.FIRSTNAME,
        label:
          "First / given name (as shown in the national identification document)",
        idPrefix: "director_firstname",
        regex: NAME_REGEX,
        getValue: (director) => director?.firstName || "",
        getError: (errors, index) =>
          errors[`shareholdersDirectors.${index}.firstName`],
      },
      {
        field: ShareholderDirectorField.LASTNAME,
        label: "Last name (as shown in the national identification document)",
        idPrefix: "director_lastname",
        regex: NAME_REGEX,
        getValue: (director) => director?.lastName || "",
        getError: (errors, index) =>
          errors[`shareholdersDirectors.${index}.lastName`],
      },
      {
        field: ShareholderDirectorField.EMAIL,
        label: "Email",
        idPrefix: "director_email",
        regex: EMAIL_REGEX,
        getValue: (director) => director?.email || "",
        getError: (errors, index) =>
          errors[`shareholdersDirectors.${index}.email`],
      },
    ],
    []
  );

  /**
   * Creates a memoized handler for field updates
   * Reduces function recreation on each render
   */
  const createFieldChangeHandler = useCallback(
    (index: number, config: FieldConfig) => (value: string) => {
      if (!isReadOnly) {
        const sanitizedValue = value.replace(config.regex, "");
        const finalValue =
          config.field === ShareholderDirectorField.EMAIL
            ? sanitizedValue.trim()
            : sanitizedValue;
        updateShareholderDirectorValue(index, config.field, finalValue);
      }
    },
    [isReadOnly, updateShareholderDirectorValue]
  );

  return (
    <>
      <p className={classes.shareTitle}>Director Info</p>
      <Group position="left" mb={5} style={{ marginTop: "40px" }}>
        <Text
          className={classes.text}
          component="label"
          htmlFor="shareholder"
          size="sm"
        >
          Total no. of Directors
        </Text>
      </Group>
      <NumberInput
        onKeyDown={handleKeyDown}
        error={error}
        id="shareholderDirector"
        min={1}
        max={50}
        placeholder=""
        className={classes.inputField}
        value={numOfShareholdersDirectors}
        size="lg"
        onChange={onNumberOfShareholdersDirectorsChange}
        disabled={isReadOnly}
        styles={{
          input: {
            caretColor: "transparent",
          },
        }}
      />
      {numOfShareholdersDirectors > 0 && numOfShareholdersDirectors <= 50 && (
        <>
          {Array.from({ length: numOfShareholdersDirectors }).map(
            (_, index) => {
              const director = shareholdersDirectors[index];

              return (
                <div key={index} className={classes.shareholderDiv}>
                  {fieldConfigs.map((config) => {
                    const fieldError = config.getError(errors, index);

                    return (
                      <div
                        key={config.field}
                        className={classes.inputGroupField}
                      >
                        <InputGroup
                          onChange={createFieldChangeHandler(index, config)}
                          labelText={config.label}
                          id={`${config.idPrefix}_${index}`}
                          value={config.getValue(director)}
                          readOnly={isReadOnly}
                          isErrored={fieldError}
                          errorMessage={!!fieldError}
                          shorterLength={
                            config.label.length <= 5 ? true : false
                          }
                        />
                      </div>
                    );
                  })}
                  {!isReadOnly &&
                    numOfShareholdersDirectors > 1 &&
                    onRemoveDirector && (
                      <div className={classes.deleteButtonContainer}>
                        <Tooltip label="Remove Director" position="top">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => onRemoveDirector(index)}
                            aria-label="Remove director"
                          >
                            <IconX size={20} />
                          </ActionIcon>
                        </Tooltip>
                      </div>
                    )}
                </div>
              );
            }
          )}
        </>
      )}
    </>
  );
};

export default ShareholdersDirectors;
